import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  authApi,
  setToken,
  clearToken,
  getToken,
  cacheUser,
  getCachedUser,
  isNetworkError,
  type User,
  type ApiError,
} from '@/api/client';
import { defaultAdminPath, DISPATCH_UI_PERMISSIONS } from '@/pages/navItems';

export type Role = string;

const INTERNAL_ROLES = ['Admin', 'Dispatcher', 'Finance', 'Ops Manager'];
const CREW_ROLES = ['Crew'];
const SALES_ROLES = ['Sales Agent'];
const CLIENT_ROLES = ['Client Main', 'Client Sub', 'Client Viewer'];

export function isInternalRole(role: string): boolean {
  return INTERNAL_ROLES.includes(role);
}

export function isCrewRole(role: string): boolean {
  return CREW_ROLES.includes(role);
}

export function isSalesRole(role: string): boolean {
  return SALES_ROLES.includes(role);
}

export function isClientRole(role: string): boolean {
  return CLIENT_ROLES.includes(role);
}

function permOn(permissions: Record<string, boolean> | undefined, key: string): boolean {
  return Boolean(permissions?.[key]);
}

export function getDefaultRoute(
  role: string,
  permissions?: Record<string, boolean>,
): string {
  if (isCrewRole(role)) return '/crew';
  if (isClientRole(role)) {
    if (permOn(permissions, 'portal.view_trips')) return '/portal';
    if (permOn(permissions, 'portal.download_docs')) return '/portal/documents';
    return '/portal';
  }
  if (isSalesRole(role)) return '/app/sales';

  const has = (p: string) => permOn(permissions, p);
  if (DISPATCH_UI_PERMISSIONS.some(has)) return '/app/dispatch';
  if (
    has('billing.run') ||
    has('pay.run') ||
    has('margin.view') ||
    has('settings.manage') ||
    has('fleet.manage') ||
    has('clients.manage')
  ) {
    return defaultAdminPath(has);
  }
  return '/app/dispatch';
}

function isAuthFailure(err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  return status === 401 || status === 403;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  offlineSession: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasRole: (roles: Role[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: readonly string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Show cached crew/admin session immediately instead of blocking on /auth/me. */
function buildInitialAuthState(): AuthState {
  const token = getToken();
  if (!token) {
    return { user: null, loading: false, error: null, offlineSession: false };
  }
  const cached = getCachedUser();
  if (cached) {
    return {
      user: cached,
      loading: false,
      error: null,
      offlineSession: typeof navigator !== 'undefined' && !navigator.onLine,
    };
  }
  return { user: null, loading: true, error: null, offlineSession: false };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(buildInitialAuthState);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      cacheUser(null);
      setState({ user: null, loading: false, error: null, offlineSession: false });
      return;
    }
    try {
      const user = await authApi.me();
      cacheUser(user);
      setState({ user, loading: false, error: null, offlineSession: false });
    } catch (err) {
      // Real auth rejection → clear session. Network/offline failure → keep
      // the last known user so crew PWA can still open for scan/POD capture.
      if (isAuthFailure(err)) {
        clearToken();
        setState({ user: null, loading: false, error: null, offlineSession: false });
        return;
      }
      const cached = getCachedUser();
      if (cached) {
        setState({ user: cached, loading: false, error: null, offlineSession: isNetworkError(err) });
        return;
      }
      setState({ user: null, loading: false, error: null, offlineSession: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Re-hydrate session when connectivity returns after an offline PWA session.
  useEffect(() => {
    const onOnline = () => {
      if (getToken()) {
        void refresh();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  const login = async (email: string, password: string) => {
    // No global loading flag here: PublicOnly would swap the login form for a
    // spinner, unmounting it and wiping the typed email and the error message.
    setState((s) => ({ ...s, error: null }));
    try {
      const res = await authApi.login(email, password);
      setToken(res.token);
      cacheUser(res.user);
      setState({ user: res.user, loading: false, error: null, offlineSession: false });
    } catch (err) {
      const apiErr = err as ApiError;
      clearToken();
      setState({ user: null, loading: false, error: apiErr.message ?? 'Login failed', offlineSession: false });
      throw err;
    }
  };

  const logout = async () => {
    clearToken();
    setState({ user: null, loading: false, error: null, offlineSession: false });
    try {
      await authApi.logout();
    } catch {
      /* cookie clear is best-effort */
    }
  };

  const hasRole = (roles: Role[]) => {
    if (!state.user) return false;
    return roles.includes(state.user.role);
  };

  const hasPermission = (permission: string) => {
    return Boolean(state.user?.permissions?.[permission]);
  };

  const hasAnyPermission = (permissions: readonly string[]) => {
    return permissions.some((p) => hasPermission(p));
  };

  return (
    <AuthContext.Provider
      value={{ ...state, login, logout, refresh, hasRole, hasPermission, hasAnyPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
